import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useAdminNotifications } from '@/lib/useNotifications';
import { LayoutDashboard, UtensilsCrossed, Clock, Image, CalendarCheck, ShoppingBag, Settings, LogOut, ArrowLeft, Menu, X, Bell, BellOff, Volume2, VolumeX, Megaphone, Users, Home, AlertTriangle, Sun, Moon, MessageSquare, ScanText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { adminApi } from '@/lib/api';

const sidebarLinks = [
  { href: '/admin', label: 'Tableau de bord', icon: LayoutDashboard, module: 'DASHBOARD', badgeKey: null },
  { href: '/admin/homepage', label: 'Page d\'accueil', icon: Home, module: 'HOMEPAGE_SECTIONS', badgeKey: null },
  { href: '/admin/actus', label: 'Actualités', icon: Megaphone, module: 'ACTUS', badgeKey: null },
  { href: '/admin/horaires', label: 'Horaires', icon: Clock, module: 'HOURS', badgeKey: null },
  { href: '/admin/photos', label: 'Photos', icon: Image, module: 'PHOTOS', badgeKey: null },
  { href: '/admin/menu', label: 'Menu', icon: UtensilsCrossed, module: 'MENU', badgeKey: null },
  { href: '/admin/plat-du-jour', label: 'Plat du Jour', icon: UtensilsCrossed, module: 'MENU', badgeKey: null },
  { href: '/admin/ocr-menu', label: 'OCR Menu', icon: ScanText, module: 'MENU', badgeKey: null },
  { href: '/admin/reservations', label: 'Réservations', icon: CalendarCheck, module: 'RESERVATIONS', badgeKey: 'pendingReservations', requiresModule: true },
  { href: '/admin/commandes', label: 'Commandes', icon: ShoppingBag, module: 'ORDERS', badgeKey: 'pendingOrders', requiresModule: true },
  { href: '/admin/utilisateurs', label: 'Utilisateurs', icon: Users, module: 'ADMIN_USERS', badgeKey: null, adminOnly: true },
  { href: '/admin/reviews', label: 'Avis clients', icon: MessageSquare, module: 'REVIEWS', badgeKey: null },
  { href: '/admin/parametres', label: 'Paramètres', icon: Settings, module: 'SETTINGS_CONTENT', badgeKey: null },
];

function NotificationBadge({ count }) {
  if (!count || count <= 0) return null;
  return (
    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center animate-pulse">
      {count > 9 ? '9+' : count}
    </span>
  );
}

export default function AdminLayout({ children, title }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasPermission, isModuleEnabled, canAccessModule } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const notifications = useAdminNotifications(true);
  const [emailWarning, setEmailWarning] = useState(false);
  const [emailDismissed, setEmailDismissed] = useState(false);
  
  // Mode Sombre State & Logic
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' ||
        (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  useEffect(() => {
    adminApi.getEmailStatus()
      .then(r => setEmailWarning(!r.data?.configured))
      .catch(() => {});
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const getBadgeCount = (badgeKey) => {
    if (!badgeKey) return 0;
    return notifications[badgeKey] || 0;
  };

  // Filter sidebar links based on permissions and module flags
  const visibleLinks = sidebarLinks.filter(link => {
    // Admin-only links
    if (link.adminOnly && user?.role !== 'ADMIN') return false;
    
    // Check module flag for reservation/orders
    if (link.requiresModule && !isModuleEnabled(link.module)) return false;
    
    // Check permission
    return hasPermission(link.module, 'view');
  });

  return (
    <div className="min-h-screen bg-cream dark:bg-[#121212] flex transition-colors duration-200">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-white dark:bg-[#1a1a1a] border-r border-garden-200 dark:border-zinc-800 p-6 fixed h-full" data-testid="admin-sidebar">
        <Link to="/" className="font-accent text-xl text-garden-800 dark:text-sunny mb-1">Côté Terrasse</Link>
        <span className="text-xs text-garden-500 dark:text-garden-400 font-medium mb-6">Administration</span>

        {/* Notification bar */}
        {notifications.totalPending > 0 && (
          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-xl px-3 py-2 mb-4 flex items-center gap-2" data-testid="notification-bar">
            <Bell size={14} className="text-red-500 animate-bounce" />
            <span className="text-xs text-red-700 dark:text-red-400 font-medium">
              {notifications.pendingOrders > 0 && `${notifications.pendingOrders} commande(s)`}
              {notifications.pendingOrders > 0 && notifications.pendingReservations > 0 && ' + '}
              {notifications.pendingReservations > 0 && `${notifications.pendingReservations} résa.`}
            </span>
          </div>
        )}

        <nav className="flex flex-col gap-1 flex-1 overflow-y-auto">
          {visibleLinks.map((link) => {
            const Icon = link.icon;
            const active = location.pathname === link.href;
            const badge = getBadgeCount(link.badgeKey);
            return (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`admin-sidebar-${link.label.toLowerCase().replace(/\s/g, '-').replace(/'/g, '')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all relative ${
                  active ? 'bg-garden-800 text-white dark:bg-garden-900' : 'text-charcoal dark:text-zinc-300 hover:bg-garden-100 dark:hover:bg-zinc-800/50'
                }`}
              >
                <div className="relative">
                  <Icon size={18} />
                  {badge > 0 && !active && <NotificationBadge count={badge} />}
                </div>
                {link.label}
                {badge > 0 && active && (
                  <span className="ml-auto bg-white/20 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{badge}</span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-garden-200 dark:border-zinc-800 pt-4 mt-4 space-y-2">
          {/* Theme toggle */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setIsDark(!isDark)}
                  data-testid="theme-toggle-btn"
                  className="flex items-center gap-2 text-xs w-full px-2 py-1.5 rounded-lg text-charcoal/70 dark:text-zinc-400 hover:bg-garden-50 dark:hover:bg-zinc-800 transition-colors"
                >
                  {isDark ? <Sun size={14} className="text-sunny" /> : <Moon size={14} className="text-garden-800" />}
                  Mode {isDark ? 'clair' : 'sombre'}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-charcoal text-white text-xs">
                Activer le thème {isDark ? 'clair' : 'sombre'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Sound toggle */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={notifications.toggleSound}
                  data-testid="sound-toggle-btn"
                  className={`flex items-center gap-2 text-xs w-full px-2 py-1.5 rounded-lg transition-colors ${
                    notifications.soundEnabled ? 'text-garden-600 hover:bg-garden-50 dark:hover:bg-zinc-800' : 'text-charcoal/40 dark:text-zinc-500 hover:bg-gray-50 dark:hover:bg-zinc-800'
                  }`}
                >
                  {notifications.soundEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
                  Son {notifications.soundEnabled ? 'activé' : 'désactivé'}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-charcoal text-white text-xs">
                {notifications.soundEnabled ? 'Cliquez pour désactiver les sons' : 'Cliquez pour activer les sons'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {user && (
            <div className="flex items-center gap-2 mb-3">
              {user.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />}
              <span className="text-sm text-charcoal dark:text-zinc-300 truncate">{user.name}</span>
            </div>
          )}
          <div className="flex gap-2">
            <Link to="/" className="flex items-center gap-1 text-xs text-garden-600 dark:text-garden-400 hover:text-garden-800 dark:hover:text-sunny">
              <ArrowLeft size={14} /> Site public
            </Link>
            <button onClick={handleLogout} data-testid="admin-logout-btn" className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 ml-auto">
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white dark:bg-[#1a1a1a] border-b border-garden-200 dark:border-zinc-800 z-50 px-4 py-3 flex items-center justify-between transition-colors duration-200">
        <button onClick={() => setMobileOpen(!mobileOpen)} className="p-3 -ml-2 text-garden-800 dark:text-sunny" data-testid="admin-mobile-toggle">
          {mobileOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
        <span className="font-accent text-lg text-garden-800 dark:text-sunny">Admin</span>
        <div className="flex items-center gap-3">
          <Link to="/" className="p-2 text-garden-600 dark:text-sunny">
            <Home size={20} />
          </Link>
          <div className="relative">
            {notifications.totalPending > 0 && (
              <div className="flex items-center gap-1">
                <Bell size={18} className="text-red-500 animate-bounce" />
                <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{notifications.totalPending}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileOpen(false)}>
          <div className="w-[280px] bg-white dark:bg-[#1a1a1a] h-full p-6 pt-20 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <nav className="flex flex-col gap-2">
              {visibleLinks.map((link) => {
                const Icon = link.icon;
                const active = location.pathname === link.href;
                const badge = getBadgeCount(link.badgeKey);
                return (
                  <Link key={link.href} to={link.href} onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-4 px-4 py-4 rounded-2xl text-base font-medium transition-all ${active ? 'bg-garden-800 text-white dark:bg-garden-900' : 'text-charcoal dark:text-zinc-300 hover:bg-garden-100 dark:hover:bg-zinc-800/50'}`}>
                    <div className="relative">
                      <Icon size={20} />
                      {badge > 0 && <NotificationBadge count={badge} />}
                    </div>
                    {link.label}
                  </Link>
                );
              })}
            </nav>
            <div className="border-t border-garden-200 dark:border-zinc-800 pt-4 mt-4 space-y-2">
              <button onClick={notifications.toggleSound} className="flex items-center gap-2 text-xs text-garden-600 dark:text-garden-400">
                {notifications.soundEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
                Son {notifications.soundEnabled ? 'activé' : 'désactivé'}
              </button>
              <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-red-500"><LogOut size={16} /> Déconnexion</button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 p-6 pt-20 lg:pt-6">
        {title && <h1 className="text-2xl md:text-3xl font-heading font-bold text-garden-800 dark:text-sunny mb-6" data-testid="admin-page-title">{title}</h1>}
        {emailWarning && !emailDismissed && (
          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-300 dark:border-amber-900 rounded-xl p-3 mb-4 flex items-center gap-3" data-testid="email-global-warning">
            <AlertTriangle size={18} className="text-amber-600 shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">Les emails ne sont pas configures</p>
              <p className="text-xs text-amber-700 dark:text-amber-400">Les notifications (reservations, etc.) ne sont pas envoyees. <Link to="/admin/parametres" className="underline font-medium">Configurer maintenant</Link></p>
            </div>
            <button onClick={() => setEmailDismissed(true)} className="text-amber-500 hover:text-amber-700 text-lg leading-none shrink-0" data-testid="email-warning-dismiss">&times;</button>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
